import { BlockContainer } from '@/components/ui/BlockContainer'
import { ErrorComponent } from '@/components/ui/Error'
import { LinePicto } from '@/components/ui/LinePicto'
import { Loading } from '@/components/ui/Loading'
import { useDataConnectionsByArea } from '@/hooks/data/useDataConnectionsByArea'
import { useDataFullLine } from '@/hooks/data/useDataFullLine'
import { useTrackNextTripsArea } from '@/hooks/track/useTrackNextTripsArea'
import { useBooleanParam } from '@/hooks/useParam'
import { getArea, getHoveredAread } from '@/stores/areas'
import { useSelector } from '@/stores/hooks'
import type { LoadingState } from '@/stores/loading'
import { getAllStops, getStop } from '@/stores/stops'
import type { Connection } from '@common/types/Area'
import type { Stop } from '@common/types/Stop'
import { classes } from '@lc/libreact'
import { useRef } from 'react'
import { AreaConnectionNextTrips } from './AreaConnectionNextTrips'
import styles from './AreaNextTrips.scss'

export function AreaNextTrips({ area }: { area: string }) {
	const { status: statusTrips, error: errorNextTrips } =
		useTrackNextTripsArea(area)
	const {
		status: statusConnections,
		connectionsByArea,
		error: errorConnections,
	} = useDataConnectionsByArea(area)
	const statusTripsLatch = useRef(statusTrips)
	const hoveredArea = useSelector(getHoveredAread)
	const allArea = useSelector(state => getArea(state, area))
	const [pmr] = useBooleanParam('pmr', false)
	const allStops = useSelector(getAllStops)
	const connectionArea = connectionsByArea?.find(connection =>
		allArea?.lines.includes(connection.line),
	)
	const stop = useSelector(state =>
		connectionArea ? getStop(state, connectionArea.stop) : null,
	)

	if (statusTrips !== 'loading' && statusTrips !== 'pending') {
		statusTripsLatch.current = statusTrips
	}

	//if one loading is failed return error
	if (statusConnections === 'failed')
		return <ErrorComponent errorId={errorConnections} />

	// if one is pending or loading return loader
	if (statusConnections !== 'loaded')
		return (
			<BlockContainer>
				<Loading />
			</BlockContainer>
		)

	const displayTitle =
		connectionsByArea?.filter(
			connection => !pmr || allStops[connection.stop].wheelchair,
		).length !== 0

	const lines = new Map<string, Connection[]>()

	if (connectionsByArea) {
		for (const con of connectionsByArea) {
			// eslint-disable-next-line unicorn/prefer-array-some
			if ([...lines.keys()].find(x => x === con.line)) {
				lines.set(con.line, [...lines.get(con.line)!, con])
			} else {
				lines.set(con.line, [con])
			}
		}
	}

	const arrayLines = [...lines.entries()]

	return (
		<div>
			{allArea && displayTitle && (
				<p
					className={classes(
						[styles.title, hoveredArea !== allArea.id],
						[styles.hoverArea, hoveredArea === allArea.id],
						[styles.visibility, stop?.wheelchair],
					)}
				>
					{allArea.name}
				</p>
			)}
			{arrayLines.map(([line, connections], i) => (
				<AreaConnection
					key={`${i}:${line}`}
					line={line}
					connections={connections}
					statusNextTrips={statusTripsLatch.current}
					errorNextTrips={errorNextTrips}
					pmr={pmr}
					allStops={allStops}
				/>
			))}
		</div>
	)
}

function AreaConnection({
	line,
	connections,
	statusNextTrips,
	errorNextTrips,
	pmr,
	allStops,
}: {
	line: string
	connections: Connection[]
	statusNextTrips: LoadingState['status']
	errorNextTrips?: string
	pmr: boolean
	allStops: Record<string, Stop>
}) {
	const {
		status: statusLine,
		line: dataLine,
		error: errorLine,
	} = useDataFullLine(line)

	if (statusLine === 'failed') return <ErrorComponent errorId={errorLine} />
	if (statusLine !== 'loaded' || !dataLine) return <Loading />

	return (
		<BlockContainer className={styles.areaConnectionContainer} shadow>
			<div className={styles.pictoWrapper}>
				<LinePicto
					className={styles.linePicto}
					line={dataLine}
					aria-hidden="true"
				/>
			</div>
			<div className={styles.connections}>
				{connections
					.filter(
						connection =>
							!pmr || allStops[connection.stop].wheelchair,
					)
					.map(con => {
						return (
							<AreaConnectionNextTrips
								key={`${con.line}/${con.direction}/${con.stop}`}
								stop={con.stop}
								direction={con.direction}
								line={con.line}
								statusNextTrips={statusNextTrips}
								errorNextTrips={errorNextTrips}
							/>
						)
					})}
			</div>
		</BlockContainer>
	)
}
